<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <div class="ef-head">
          <router-link to="/expenselist" class="ef-back" aria-label="Back to expenses"><i class="fas fa-arrow-left"></i></router-link>
          <div>
            <h3>{{ isEdit ? "Edit expense" : "Add expense" }}</h3>
            <p>{{ isEdit ? "Change the details of this expense." : "Record money spent on running the shop." }}</p>
          </div>
        </div>

        <div v-if="loading" class="ef-card ef-loading"><i class="fas fa-spinner fa-spin"></i> Loading...</div>

        <div v-else class="ef-layout">
          <div class="ef-card ef-main">
            <!-- Amount -->
            <label class="ef-label" for="ef-amount">Amount <span class="req">*</span></label>
            <div class="ef-amount" :class="{ invalid: errors.amount }">
              <span>₹</span>
              <input
                id="ef-amount"
                ref="amountInput"
                v-model="form.amount"
                type="number"
                min="0"
                step="0.01"
                inputmode="decimal"
                placeholder="0.00"
                @input="errors.amount = ''"
                @keydown.enter.prevent="save()"
              />
            </div>
            <div v-if="errors.amount" class="ef-error">{{ errors.amount }}</div>

            <!-- Category -->
            <div class="ef-label-row">
              <label class="ef-label">Category <span class="req">*</span></label>
              <button v-if="canAddCategory" type="button" class="ef-link" @click="quickAddCategory"><i class="fas fa-plus"></i> New category</button>
            </div>
            <div class="ef-cats" :class="{ invalid: errors.categoryid }">
              <button
                v-for="category in activeCategories"
                :key="category.categoryid"
                type="button"
                class="ef-cat"
                :class="{ on: form.categoryid === category.categoryid }"
                @click="form.categoryid = category.categoryid; errors.categoryid = ''"
              >
                <i class="ef-dot" :style="{ background: categoryColor(category.categoryid) }"></i>{{ category.categoryname }}
              </button>
              <span v-if="!activeCategories.length" class="ef-muted">No categories yet — add one with “New category”.</span>
            </div>
            <div v-if="errors.categoryid" class="ef-error">{{ errors.categoryid }}</div>

            <!-- Payment mode -->
            <label class="ef-label">Paid by</label>
            <div class="ef-modes">
              <button
                v-for="mode in paymentModes"
                :key="mode.value"
                type="button"
                class="ef-mode"
                :class="{ on: form.paymentmode === mode.value }"
                @click="form.paymentmode = form.paymentmode === mode.value ? '' : mode.value"
              >
                <i :class="mode.icon"></i><span>{{ mode.label }}</span>
              </button>
            </div>

            <div class="ef-grid">
              <div>
                <label class="ef-label" for="ef-date">Date <span class="req">*</span></label>
                <input id="ef-date" v-model="form.expensedate" type="date" class="ef-input" :class="{ invalid: errors.expensedate }" />
                <div class="ef-quick">
                  <button type="button" :class="{ on: form.expensedate === todayIso }" @click="form.expensedate = todayIso">Today</button>
                  <button type="button" :class="{ on: form.expensedate === yesterdayIso }" @click="form.expensedate = yesterdayIso">Yesterday</button>
                </div>
              </div>
              <div>
                <label class="ef-label" for="ef-payee">Paid to <span class="opt">optional</span></label>
                <input id="ef-payee" v-model.trim="form.payeename" list="ef-payees" type="text" class="ef-input" placeholder="e.g. KSEB, Landlord, Driver" />
                <datalist id="ef-payees">
                  <option v-for="payee in recentPayees" :key="payee" :value="payee" />
                </datalist>
              </div>
              <div>
                <label class="ef-label" for="ef-bill">Bill / receipt no. <span class="opt">optional</span></label>
                <input id="ef-bill" v-model.trim="form.billno" type="text" class="ef-input" placeholder="e.g. 4521" />
              </div>
              <div class="ef-span">
                <label class="ef-label" for="ef-remarks">Note <span class="opt">optional</span></label>
                <textarea id="ef-remarks" v-model.trim="form.remarks" rows="2" class="ef-input" placeholder="What was it for?"></textarea>
              </div>
            </div>
          </div>

          <aside class="ef-card ef-side">
            <div class="ef-preview">
              <span class="ef-preview-dot" :style="{ background: form.categoryid ? categoryColor(form.categoryid) : '#e5e7eb' }"></span>
              <div>
                <strong>{{ selectedCategory ? selectedCategory.categoryname : "Choose a category" }}</strong>
                <small>{{ formatDate(form.expensedate) }}<template v-if="form.payeename"> &middot; {{ form.payeename }}</template></small>
              </div>
            </div>
            <div class="ef-total">
              <span>Amount</span>
              <strong>{{ money(form.amount) }}</strong>
              <small><i :class="modeOf(form.paymentmode).icon"></i> {{ form.paymentmode ? modeOf(form.paymentmode).label : "Payment mode not set" }}</small>
            </div>
            <button type="button" class="btn btn-submit w-100" :disabled="saving" @click="save()">
              <i class="fas fa-check me-1"></i>{{ saving ? "Saving..." : isEdit ? "Save changes" : "Save expense" }}
            </button>
            <button v-if="!isEdit" type="button" class="btn ef-btn-light w-100" :disabled="saving" @click="save({ another: true })">
              <i class="fas fa-plus me-1"></i>Save &amp; add another
            </button>
            <router-link to="/expenselist" class="ef-cancel">Cancel</router-link>
            <p class="ef-tip">Tip: press <kbd>Enter</kbd> in the amount box to save.</p>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { PAYMENT_MODES, paymentModeOf, categoryColor, isoDate } from "@/views/pages/expense/expenseShared";

export default {
  name: "ExpenseForm",
  props: {
    mode: { type: String, default: "add" },
  },
  data() {
    return {
      loading: this.mode === "edit",
      saving: false,
      categories: [],
      recentPayees: [],
      paymentModes: PAYMENT_MODES,
      todayIso: isoDate(new Date()),
      yesterdayIso: isoDate(new Date(Date.now() - 86400000)),
      errors: { amount: "", categoryid: "", expensedate: "" },
      form: { categoryid: "", expensedate: isoDate(new Date()), amount: "", billno: "", paymentmode: "CASH", payeename: "", remarks: "" },
    };
  },
  computed: {
    isEdit() {
      return this.mode === "edit";
    },
    expenseId() {
      return this.$route.query.id;
    },
    canAddCategory() {
      return this.$store.getters.hasPermission("expense.add");
    },
    activeCategories() {
      return this.categories.filter((c) => c.is_active !== false || c.categoryid === this.form.categoryid);
    },
    selectedCategory() {
      return this.categories.find((c) => c.categoryid === this.form.categoryid) || null;
    },
  },
  methods: {
    categoryColor,
    modeOf: paymentModeOf,
    money(value) {
      return `Rs. ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatDate(value) {
      if (!value) return "No date";
      if (value === this.todayIso) return "Today";
      if (value === this.yesterdayIso) return "Yesterday";
      return new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    async loadCategories() {
      const response = await axios.get("/v1/expenses/categories", { params: { include_inactive: true } });
      this.categories = response.data || [];
    },
    async loadRecentPayees() {
      try {
        const from = isoDate(new Date(Date.now() - 180 * 86400000));
        const response = await axios.get("/v1/expenses", { params: { date_from: from, date_to: this.todayIso } });
        const counts = new Map();
        (response.data || []).forEach((e) => { if (e.payeename) counts.set(e.payeename, (counts.get(e.payeename) || 0) + 1); });
        this.recentPayees = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 30).map(([name]) => name);
      } catch (error) {
        this.recentPayees = [];
      }
    },
    async loadExpense() {
      try {
        const { data } = await axios.get(`/v1/expenses/${this.expenseId}`);
        this.form = {
          categoryid: data.categoryid || "",
          expensedate: data.expensedate || this.todayIso,
          amount: data.amount ?? "",
          billno: data.billno || "",
          paymentmode: data.paymentmode || "",
          payeename: data.payeename || "",
          remarks: data.remarks || "",
        };
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not load expense", text: error.response?.data?.detail || error.message });
        this.$router.push("/expenselist");
      }
    },
    validate() {
      this.errors = {
        amount: !(Number(this.form.amount) > 0) ? "Enter the amount spent." : "",
        categoryid: !this.form.categoryid ? "Choose what the money was spent on." : "",
        expensedate: !this.form.expensedate ? "Choose a date." : "",
      };
      return !Object.values(this.errors).some(Boolean);
    },
    payload() {
      return {
        categoryid: this.form.categoryid,
        expensedate: this.form.expensedate,
        amount: Number(this.form.amount).toFixed(2),
        billno: this.form.billno || null,
        paymentmode: this.form.paymentmode || null,
        payeename: this.form.payeename || null,
        remarks: this.form.remarks || null,
      };
    },
    async save({ another = false } = {}) {
      if (this.saving) return;
      if (!this.validate()) return;
      this.saving = true;
      try {
        if (this.isEdit) {
          await axios.put(`/v1/expenses/${this.expenseId}`, this.payload());
        } else {
          await axios.post("/v1/expenses", this.payload());
        }
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: this.isEdit ? "Expense updated" : `Saved ${this.money(this.form.amount)}`, showConfirmButton: false, timer: 1600 });
        if (another) {
          const keep = { categoryid: this.form.categoryid, expensedate: this.form.expensedate, paymentmode: this.form.paymentmode };
          this.form = { ...keep, amount: "", billno: "", payeename: "", remarks: "" };
          this.$nextTick(() => this.$refs.amountInput?.focus());
        } else {
          this.$router.push("/expenselist");
        }
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not save", text: error.response?.data?.detail || error.message });
      } finally {
        this.saving = false;
      }
    },
    async quickAddCategory() {
      const { value: name } = await Swal.fire({
        title: "New expense category",
        input: "text",
        inputPlaceholder: "e.g. Electricity, Rent, Salary",
        showCancelButton: true,
        confirmButtonText: "Add",
        inputValidator: (value) => (!value || !value.trim() ? "Enter a name" : undefined),
      });
      if (!name) return;
      try {
        const { data } = await axios.post("/v1/expenses/categories", { categoryname: name.trim(), description: null, is_active: true });
        await this.loadCategories();
        const created = this.categories.find((c) => c.categoryid === data?.categoryid) || this.categories.find((c) => c.categoryname.toLowerCase() === name.trim().toLowerCase());
        if (created) this.form.categoryid = created.categoryid;
        this.errors.categoryid = "";
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not add category", text: error.response?.data?.detail || error.message });
      }
    },
  },
  async mounted() {
    try {
      await Promise.all([this.loadCategories(), this.loadRecentPayees(), this.isEdit ? this.loadExpense() : Promise.resolve()]);
    } finally {
      this.loading = false;
    }
    this.$nextTick(() => this.$refs.amountInput?.focus());
  },
};
</script>

<style scoped>
.ef-head { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
.ef-head h3 { margin: 0; font-size: 20px; font-weight: 700; color: #111827; }
.ef-head p { margin: 2px 0 0; font-size: 13px; color: #6b7280; }
.ef-back { width: 38px; height: 38px; flex: 0 0 38px; border-radius: 10px; border: 1px solid #e5e7eb; background: #fff; display: flex; align-items: center; justify-content: center; color: #374151; }
.ef-back:hover { border-color: #FF9F43; color: #c2410c; }
.ef-layout { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 18px; align-items: start; max-width: 1100px; }
.ef-card { background: #fff; border: 1px solid #e9ecf2; border-radius: 14px; padding: 18px 20px; }
.ef-loading { display: flex; align-items: center; gap: 10px; color: #6b7280; }
.ef-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin: 16px 0 8px; }
.ef-main > .ef-label:first-child { margin-top: 0; }
.ef-label-row { display: flex; align-items: center; justify-content: space-between; margin: 16px 0 8px; }
.ef-label-row .ef-label { margin: 0; }
.req { color: #dc2626; }
.opt { font-weight: 400; color: #9ca3af; font-size: 12px; }
.ef-link { border: 0; background: transparent; color: #c2410c; font-size: 13px; font-weight: 600; }
.ef-link:hover { text-decoration: underline; }
.ef-error { margin-top: 6px; font-size: 12.5px; color: #dc2626; }
.ef-muted { font-size: 13px; color: #9ca3af; }

.ef-amount { display: flex; align-items: center; border: 2px solid #e5e7eb; border-radius: 14px; padding: 0 16px; background: #fafafa; max-width: 360px; }
.ef-amount:focus-within { border-color: #FF9F43; background: #fff; box-shadow: 0 0 0 4px rgba(255, 159, 67, 0.12); }
.ef-amount.invalid { border-color: #f87171; }
.ef-amount span { font-size: 24px; color: #9ca3af; font-weight: 600; }
.ef-amount input { flex: 1; min-width: 0; border: 0; background: transparent; height: 60px; padding: 0 10px; font-size: 30px; font-weight: 700; color: #111827; font-variant-numeric: tabular-nums; }
.ef-amount input:focus { outline: none; }

.ef-cats { display: flex; flex-wrap: wrap; gap: 8px; padding: 2px; border-radius: 12px; }
.ef-cats.invalid { box-shadow: 0 0 0 2px #fecaca; }
.ef-cat { border: 1.5px solid #e5e7eb; background: #fff; border-radius: 999px; padding: 7px 14px; font-size: 13.5px; font-weight: 600; color: #374151; display: inline-flex; align-items: center; gap: 8px; }
.ef-cat:hover { border-color: #fdba74; }
.ef-cat.on { border-color: #FF9F43; background: #fff4e8; color: #9a3412; }
.ef-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }

.ef-modes { display: flex; flex-wrap: wrap; gap: 8px; }
.ef-mode { display: inline-flex; align-items: center; gap: 8px; border: 1.5px solid #e5e7eb; background: #fff; border-radius: 10px; padding: 9px 14px; font-size: 13.5px; font-weight: 600; color: #4b5563; }
.ef-mode i { color: #9ca3af; }
.ef-mode.on { border-color: #FF9F43; background: #fff4e8; color: #9a3412; }
.ef-mode.on i { color: #c2410c; }

.ef-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0 16px; }
.ef-span { grid-column: 1 / -1; }
.ef-input { width: 100%; min-height: 40px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 12px; font-size: 14px; background: #fff; }
.ef-input:focus { outline: none; border-color: #FF9F43; box-shadow: 0 0 0 3px rgba(255, 159, 67, 0.12); }
.ef-input.invalid { border-color: #f87171; }
.ef-quick { display: flex; gap: 6px; margin-top: 6px; }
.ef-quick button { border: 1px solid #e5e7eb; background: #fff; border-radius: 999px; padding: 2px 10px; font-size: 12px; color: #6b7280; }
.ef-quick button.on { border-color: #111827; background: #111827; color: #fff; }

.ef-side { position: sticky; top: 80px; display: flex; flex-direction: column; gap: 10px; }
.ef-preview { display: flex; align-items: center; gap: 12px; }
.ef-preview-dot { width: 40px; height: 40px; border-radius: 12px; flex: 0 0 40px; opacity: 0.9; }
.ef-preview strong { display: block; font-size: 15px; color: #111827; }
.ef-preview small { font-size: 12.5px; color: #6b7280; }
.ef-total { display: flex; flex-direction: column; gap: 2px; padding: 14px; border-radius: 12px; background: #fff7ed; color: #9a3412; }
.ef-total span { font-size: 12px; font-weight: 600; text-transform: uppercase; }
.ef-total strong { font-size: 26px; font-variant-numeric: tabular-nums; }
.ef-total small { font-size: 12.5px; color: #b45309; }
.ef-side .btn { min-height: 44px; font-weight: 600; }
.ef-btn-light { border: 1px solid #e5e7eb; background: #fff; color: #374151; }
.ef-btn-light:hover { border-color: #FF9F43; color: #c2410c; }
.ef-cancel { text-align: center; font-size: 13px; color: #6b7280; }
.ef-tip { margin: 0; text-align: center; font-size: 11.5px; color: #9ca3af; }
.ef-tip kbd { padding: 1px 5px; border-radius: 4px; background: #f3f4f6; border: 1px solid #e5e7eb; color: #374151; font-size: 11px; }

@media (max-width: 991px) {
  .ef-layout { grid-template-columns: 1fr; }
  .ef-side { position: sticky; bottom: 0; top: auto; z-index: 5; box-shadow: 0 -6px 18px rgba(15, 23, 42, 0.08); }
  .ef-preview, .ef-tip { display: none; }
  .ef-grid { grid-template-columns: 1fr; }
}
</style>
